import React, { useState, useEffect } from 'react';
import { HelpCircle, AlertCircle, Loader } from 'react-feather';
import { getInputFieldStyles } from '../InputField/styleUtils';
import { InputFieldSize } from '../InputField/theme';
import { theme } from '../InputField/theme';
import '../InputField/InputField.css';
import useCurrencyHelpers from './utils.ts';

export interface MoneyInputFieldProps {
	label?: string;
	placeholder?: string;
	hint?: string;
	tooltipText?: string;
	endAction?: React.ReactNode;
	loading?: boolean;
	error?: boolean;
	disabled?: boolean;
	required?: boolean;
	type?: 'text' | 'password' | 'email' | 'number' | 'date';
	value?: string;
	onChange: (value: string | null) => void;
	name?: string;
	id?: string;
	size?: InputFieldSize;
	maxChars?: number;
	charLenghtError?: string;
	shape?: 'pill' | 'default';
	focused?: boolean;
	dataTestId?: string;
	pill?: boolean;
	autofocus?: boolean;
	strictMaxChars?: boolean;
	hasCharLengthError?: (error: boolean) => void;
}

const MoneyInputField: React.FC<MoneyInputFieldProps> = ({
	label,
	placeholder,
	hint,
	tooltipText,
	endAction,
	loading,
	value,
	onChange,
	error,
	disabled,
	required,
	type,
	name,
	id,
	size,
	maxChars = 20000,
	shape,
	focused = false,
	dataTestId,
	autofocus,
	strictMaxChars = false
}) => {
	const [isFocused, setIsFocused] = useState(focused);
	const [inputValue, setInputValue] = useState<string | null>(null);
	const { parseToCleanString, format, sanitizeInput, parseToMoneyBeforeSubmit } =
		useCurrencyHelpers({ locale: 'es-MX', maximumFractionDigits: 2, currency: 'MXN' });
	const dynamicStyles = getInputFieldStyles(theme, size, shape);

	useEffect(() => {
		if (value !== undefined && value !== inputValue && inputValue === null) {
			setInputValue(format(value || ''));
		}
	}, [value, inputValue]);

	const componentStyles = () => {
		const baseStyles = dynamicStyles.input_css;
		const focusedStyles = isFocused && !error ? dynamicStyles.inputFocused_css : {};
		const disabledStyles = disabled || loading ? dynamicStyles.inputDisabled_css : {};
		const errorStyles = error ? dynamicStyles.inputError_css : {};
		const errorFocusedStyles = isFocused && error ? dynamicStyles.errorFocused_css : {};

		return {
			...baseStyles,
			...focusedStyles,
			...disabledStyles,
			...errorStyles,
			...errorFocusedStyles
		};
	};

	const renderIcon = (
		loading: boolean | undefined,
		error: boolean | undefined,
		tooltipText: string | undefined
	) => {
		if (error) {
			return <AlertCircle size={16} color={dynamicStyles.icons.error} data-testid="error-icon" />;
		}

		if (loading && !error) {
			return (
				<Loader
					size={16}
					color={dynamicStyles.icons.loading}
					className="spin-animation"
					data-testid="loading-icon"
				/>
			);
		}

		if (tooltipText && !error) {
			return (
				<HelpCircle size={16} color={dynamicStyles.icons.tooltip} data-testid="tooltip-icon" />
			);
		}

		return null;
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Enter' && !error) {
			event.currentTarget.blur();
		}
	};

	return (
		<div className="input-container">
			<label
				htmlFor={id}
				className="input-label"
				id="input-label"
				style={dynamicStyles.inputLabel_css}
			>
				{label}
			</label>
			<div style={componentStyles()} className={'input'}>
				<input
					autoFocus={autofocus}
					data-testid={dataTestId}
					aria-label={label || 'input'}
					aria-describedby="input-label"
					aria-invalid={error ? 'true' : 'false'}
					aria-required={required ? 'true' : 'false'}
					onFocus={() => {
						setIsFocused(true);
						setInputValue(parseToCleanString(inputValue || ''));
					}}
					onBlur={() => {
						setInputValue(format(inputValue || ''));
						onChange(parseToMoneyBeforeSubmit(inputValue || ''));
						setIsFocused(false);
					}}
					onChange={(e) => setInputValue(sanitizeInput(e.target.value))}
					onKeyDown={handleKeyDown}
					type={'text'}
					min={type === 'number' ? 1 : undefined}
					placeholder={placeholder}
					value={inputValue as string}
					name={name}
					id={id}
					maxLength={strictMaxChars ? maxChars : undefined}
					disabled={disabled || loading}
					required={required}
					className="input-field"
				/>
				{renderIcon(loading, error, tooltipText)}
				{endAction}
			</div>
			<span
				style={{
					...dynamicStyles.inputHint_css,
					...(error ? dynamicStyles.inputHintError_css : {})
				}}
			>
				{hint}
			</span>
		</div>
	);
};

export default MoneyInputField;
